import { useState, useContext, useMemo, useCallback, useRef } from 'react';
import { Button, Icon, Segment, Input, Dropdown, Checkbox }   from 'semantic-ui-react';
import { useDebounce }                                        from 'use-debounce';
import styled                                                 from 'styled-components';
import { Link }                                               from 'react-router-dom';
import CenterCell                                             from '../../../components/table/CenterCell';
import ServerPaginatedTable                                   from '../../../components/table/ServerPaginatedTable';
import TestsModal                                             from './TestsModal';
import AppContext                                             from '../../../context/HeaderContext';
import Duration                                               from '../../../components/Duration';
import { getAPI }                                             from '../../../libs/fetchAPI';
import RightCell                                              from '../../../components/table/RightCell';

const CustomCenterCell = styled(CenterCell)`
	width : 100%;
	min-width : 205px;
`;

const CustomFormItem = styled.span`
	margin-right: 25px;
	min-width: 400px !important;
`;

const CustomWidthInput = styled(Input)`
	min-width: 400px !important;
`;

const CustomSegment = styled(Segment)`
	padding: 1.5em 1em !important;
`;

const FilterHeader = styled.span`
	font-weight: bold;
	color: grey;
	margin-left: 5px;
	font-size: 1.2em;
	fong-family: Lato,'Helvetica Neue',Arial,Helvetica,sans-serif;
`;

const CustomDropdown = styled(Dropdown)`
	min-width: 250px !important;
`;

function AllSpecs({ handleSetSpecName }) {
	const { current_project_id }                                           = useContext(AppContext);
	const [ loading, setLoading ]                                          = useState(false);
	const [ show_tests_modal, setShowTestsModal ]                          = useState(false);
	const [ current_suite_params, setCurrentSuiteParams ]                  = useState();
	const [ suites, setSuites ]                                            = useState([]);
	const [ page_count, setPageCount ]                                     = useState(1);
	const [ date_range, setDateRange ]                                     = useState(`7`);
	const [ search, setSearch ]                                            = useState(``);
	const [ average_duration_less_than, setAverageDurationLessThan ]       = useState([]);
	const [ average_duration_greater_than, setAverageDurationGreaterThan ] = useState([]);
	const [ retry_percent_less_than, setRetryPercentLessThan ]             = useState([]);
	const [ retry_percent_greater_than, setRetryPercentGreaterThan ]       = useState([]);
	const [ report_active, setReportActive ]                               = useState(true);
	const [ report_inactive, setReportInactive ]                           = useState(true);
	const [ debounced_search ]                                             = useDebounce(search, 500);
	const fetch_id_ref                                                     = useRef(0);

	handleSetSpecName(``);

	const fetchTableData = useCallback(({ pageIndex, pageSize, filters, sortBy }) => {
		const fetch_id = ++fetch_id_ref.current;

		if(fetch_id !== fetch_id_ref.current) {
			return;
		}

		let sort_key       = `spec_file`;
		let sort_direction = `DESC`;

		if(sortBy.length) {
			sort_key       = sortBy[0].id;
			sort_direction = sortBy[0].desc ? `DESC` : `ASC`;
		}

		const params = {
			project_id     : current_project_id,
			limit          : pageSize,
			offset         : pageSize * pageIndex,
			filters        : JSON.stringify(filters),
			search         : debounced_search,
			date_range,
			average_duration_less_than,
			average_duration_greater_than,
			retry_percent_less_than,
			retry_percent_greater_than,
			report_active,
			report_inactive,
			sort_key,
			sort_direction,
		};

		setLoading(true);
		getAPI(`/suite_info?${new URLSearchParams(params).toString()}`)
			.then(({ data, pages }) => {
				setSuites(data);
				setPageCount(pages);
				setLoading(false);
			});
	}, [current_project_id, debounced_search, date_range, average_duration_less_than, average_duration_greater_than, retry_percent_less_than, retry_percent_greater_than, report_active, report_inactive]);

	function handleViewTests(params) {
		setShowTestsModal(true);
		setCurrentSuiteParams(params);
	}

	function handleCloseTests() {
		setShowTestsModal(false);
	}

	function handleReportChange(value, fn) {
		fn(value);
		fetchTableData({
			pageIndex : 0,
			pageSize  : 15,
			filters   : [],
			sortBy    : [],
		});
	}

	/* eslint-disable */
	const columns = useMemo(() => [
		{
			Header   : `Spec File`,
			accessor : `spec_file`,
		},
		{
			Header   : `Title`,
			accessor : `title`,
		},
		{
			Header   : `Version`,
			accessor : `version`,
			Cell     : ({ cell }) => <RightCell>{cell.value || `N/A`}</RightCell>
		},
		{
			Header   : `Passes`,
			accessor : `passes`,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Fails`,
			accessor : `fails`,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Skipped`,
			accessor : `skips`,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Retries`,
			accessor : `retries`,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Retry %`,
			accessor : `retry_percent`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell>{cell.value}%</RightCell>
		},
		{
			Header   : `Pass %`,
			accessor : `pass_percent`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell>{cell.value}%</RightCell>
		},
		{
			Header   : `Average Duration`,
			accessor : `average_duration`,
			Cell     : ({ cell }) => <RightCell><Duration duration={cell.value} /></RightCell>
		},
		{
			Header         : `Active`,
			accessor       : `disabled`,
			collapse       : true,
			disableFilters : true,
			Cell           : ({ cell }) => {
				const name  = !cell.value ? `check` : `cancel`;
				const color = !cell.value ? `green` : `red`;

				return <CenterCell><Icon name={name} color={color} /></CenterCell>
			}
		},
		{
			Header         : ``,
			accessor       : `id`,
			collapse       : true,
			disableFilters : true,
			Cell           : ({ cell }) => {
				const row    = cell.row.values;
				const params = {
					spec_file  : row.spec_file,
					version    : row.version || ``,
					project_id : current_project_id,
				};

				const link = `/specs/single?${new URLSearchParams(params)}`;

				return (
					<CustomCenterCell>
						<Button color="teal" onClick={() => handleViewTests(params)}>Tests</Button>
						<Link to={link}><Button color="blue">History</Button></Link>
					</CustomCenterCell>
				)
			}
		}
	], []);
	/* eslint-enable */

	const date_options = [
		{
			key   : `7d`,
			text  : `Last 7 days`,
			value : `7`,
		},
		{
			key   : `14d`,
			text  : `Last 14 days`,
			value : `14`,
		},
		{
			key   : `30d`,
			text  : `Last 30 days`,
			value : `30`,
		},
		{
			key   : `60d`,
			text  : `Last 60 days`,
			value : `60`,
		},
		{
			key   : `90d`,
			text  : `Last 90 days`,
			value : `90`,
		},
		{
			key   : `180d`,
			text  : `Last 180 days`,
			value : `180`,
		}
	];

	const duration_options = [
		{
			key   : `30`,
			text  : `30 seconds`,
			value : `30`,
		},
		{
			key   : `60`,
			text  : `1 minute`,
			value : `60`,
		},
		{
			key   : `120`,
			text  : `2 minutes`,
			value : `120`,
		},
		{
			key   : `180`,
			text  : `3 minutes`,
			value : `180`,
		},
		{
			key   : `300`,
			text  : `5 minutes`,
			value : `300`,
		},
		{
			key   : `600`,
			text  : `10 minutes`,
			value : `180`,
		},
	];

	const retry_options = [
		{
			key   :	`2`,
			text  : `2%`,
			value : `2`,
		},
		{
			key   :	`5`,
			text  : `5%`,
			value : `5`,
		},
		{
			key   :	`10`,
			text  : `10%`,
			value : `10`,
		},
		{
			key   :	`15`,
			text  : `15%`,
			value : `15`,
		},
	]

	const average_duration_less_than_options    = duration_options.map(({ key, text, value}) => { return { key, text: `<= ${text}`, value } });
	const average_duration_greater_than_options = duration_options.map(({ key, text, value}) => { return { key, text: `>= ${text}`, value } });;
	const retry_percent_less_than_options       = retry_options.map(({ key, text, value}) => { return { key, text: `<= ${text}`, value } });
	const retry_percent_greater_than_options    = retry_options.map(({ key, text, value}) => { return { key, text: `>= ${text}`, value } });

	return (
		<>
			<TestsModal
				open={show_tests_modal}
				suite_params={current_suite_params}
				handleClose={handleCloseTests}
			/>

			<CustomSegment>
				<div>
					<Icon name="filter" color="grey" />
					<FilterHeader>Filter</FilterHeader>
				</div>

				<br></br>
				<div>
					<CustomFormItem>
						<CustomDropdown
							placeholder='Date Range'
							selection
							onChange={(e, data) => handleReportChange(data.value, setDateRange)}
							options={date_options}
							value={date_range}
						/>
					</CustomFormItem>

					<CustomFormItem>
						<CustomWidthInput
							placeholder="Title or Spec File"
							onChange={(e, data) => handleReportChange(data.value, setSearch)}
							value={search}
						/>
					</CustomFormItem>
				</div>
				<br></br>
				<div>
					<CustomFormItem>
						<CustomDropdown
							placeholder="Average Duration Less Than"
							multiple
							search
							selection
							clearable
							onChange={(e, data) => handleReportChange(data.value, setAverageDurationLessThan)}
							value={average_duration_less_than}
							options={average_duration_less_than_options}
						/>
					</CustomFormItem>

					<CustomFormItem>
						<CustomDropdown
							placeholder="Average Duration Greater Than"
							multiple
							search
							selection
							clearable
							onChange={(e, data) => handleReportChange(data.value, setAverageDurationGreaterThan)}
							value={average_duration_greater_than}
							options={average_duration_greater_than_options}
						/>
					</CustomFormItem>
					<CustomFormItem>
						<CustomDropdown
							placeholder="Retry Percent Less Than"
							multiple
							search
							selection
							onChange={(e, data) => handleReportChange(data.value, setRetryPercentLessThan)}
							value={retry_percent_less_than}
							options={retry_percent_less_than_options}
						/>
					</CustomFormItem>

					<CustomFormItem>
						<CustomDropdown
							placeholder="Retry Percent Greater Than"
							multiple
							search
							selection
							onChange={(e, data) => handleReportChange(data.value, setRetryPercentGreaterThan)}
							value={retry_percent_greater_than}
							options={retry_percent_greater_than_options}
						/>
					</CustomFormItem>
				</div>

				<br></br>
				<div>
					<CustomFormItem>
						<Checkbox
							label="Active"
							defaultChecked
							onChange={(e, data) => handleReportChange(data.checked, setReportActive)}
						/>
					</CustomFormItem>

					<CustomFormItem>
						<Checkbox
							label="Inactive"
							defaultChecked
							onChange={(e, data) => handleReportChange(data.checked, setReportInactive)}
						/>
					</CustomFormItem>
				</div>
			</CustomSegment>

			<ServerPaginatedTable
				columns={columns}
				data={suites}
				fetchTableData={fetchTableData}
				loading={loading}
				pageCount={page_count}
				current_project_id={current_project_id}
				default_page_size="10"
			/>
		</>
	);
}

export default AllSpecs;
